import type { SickHoursExportSuffix, SickHoursRow, SickTimesheetStage } from '../../api/sickHours';
import type { StatusKey } from '../EmployeeTimesheetReviewConsole';

/**
 * Pure helpers for the v2 Sick Hours page (WCDP payroll). No React, no DOM.
 *
 * Rules from docs/featureBriefs/design_handoff_sick_hours_page_design/README.md
 * (the "Resolved" section at its end overrides anything earlier):
 *  - Rate state: Needs rate = approved hours > 0 and no rate; Pending only = no
 *    approved hours; otherwise the rate is Entered.
 *  - Quick filter: Needs rate · Has pending (pending hours > 0) · All. Counts follow
 *    search and column filters.
 *  - Default sort Superintendent A–Z, then Employee; every other sort breaks ties the
 *    same way. A blank superintendent always sorts last.
 *  - Dates are YYYY-MM-DD strings, only ever turned into Dates via Date.UTC.
 */

/* ------------------------------------------------------------------ format */

export const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Sentinel for "no superintendent" in the Superintendent filter. */
export const NO_SUPER = '(none)';

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

export function money(n: number | string): string {
  return '$' + Number(n).toFixed(2);
}

/** Totals: two decimals. */
export function hrs(n: number): string {
  return Number(n).toFixed(2);
}

/** Day chips: `8h`, `4.5h`, `4.25h` — up to two decimals, trailing zeros dropped. */
export function hShort(n: number): string {
  return `${Number(Number(n).toFixed(2))}h`;
}

function utc(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** 0 = Mon … 6 = Sun, from a YYYY-MM-DD date. */
export function dowIndex(date: string): number {
  return (utc(date).getUTCDay() + 6) % 7;
}

/** "10/12" */
export function mdShort(date: string): string {
  const t = utc(date);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()}`;
}

/** "Mon 10/12" */
export function dayLabel(date: string): string {
  return `${DOW[dowIndex(date)]} ${mdShort(date)}`;
}

/** The seven YYYY-MM-DD dates starting at `start`. */
export function weekDates(start: string): string[] {
  const t = utc(start);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(t.getTime() + i * 86_400_000);
    return d.toISOString().slice(0, 10);
  });
}

/** "Oct 12 – 18" or "Sep 28 – Oct 4". */
export function fmtWeekRange(start: string, end: string): string {
  const a = utc(start);
  const b = utc(end);
  const left = `${MONTHS[a.getUTCMonth()]} ${a.getUTCDate()}`;
  const right = a.getUTCMonth() === b.getUTCMonth()
    ? `${b.getUTCDate()}`
    : `${MONTHS[b.getUTCMonth()]} ${b.getUTCDate()}`;
  return `${left} – ${right}`;
}

/* ------------------------------------------------------------------ rows */

export type RateState = 'Needs rate' | 'Entered' | 'Pending only';
export const RATE_STATES: RateState[] = ['Needs rate', 'Entered', 'Pending only'];

export type StageLabel = 'Unsubmitted' | 'Pending Approval' | 'Payroll Pending' | 'Payroll Approved' | 'Exported';
export const STAGES: StageLabel[] = ['Unsubmitted', 'Pending Approval', 'Payroll Pending', 'Payroll Approved', 'Exported'];

const STAGE_LABEL: Record<SickTimesheetStage, StageLabel> = {
  PENDING_APPROVAL: 'Pending Approval',
  PAYROLL_PENDING: 'Payroll Pending',
  PAYROLL_APPROVED: 'Payroll Approved',
  EXPORTED: 'Exported',
};

/** Pill tone per stage — the board's palette; Exported is an outlined slate pill. */
export const STAGE_TONE: Record<StageLabel, string> = {
  Unsubmitted: 'neutral',
  'Pending Approval': 'info',
  'Payroll Pending': 'purple',
  'Payroll Approved': 'ok',
  Exported: 'ex',
};

export function rateState(approvedHours: number, sickRate: number | null): RateState {
  if (approvedHours > 0 && sickRate == null) return 'Needs rate';
  if (!(approvedHours > 0)) return 'Pending only';
  return 'Entered';
}

export interface ShDay { date: string; dow: number; hours: number; pending: boolean }

export interface ShRow {
  src: SickHoursRow;
  id: number;
  name: string;
  /** Business employee ID (altEmployeeID), as shown on screen. */
  empID: string;
  sup: string | null;
  /** The superintendent is the employee's own supervisor, not the timesheet's. */
  supFallback: boolean;
  days: ShDay[];
  appr: number;
  pend: number;
  total: number;
  rate: number | null;
  rateState: RateState;
  stage: StageLabel;
  /** Has a timesheet for the week, so the row opens the review console. */
  openable: boolean;
}

export function deriveRow(r: SickHoursRow): ShRow {
  const days = (r.days ?? [])
    .map((d) => ({ date: d.date.slice(0, 10), dow: dowIndex(d.date), hours: Number(d.hours) || 0, pending: d.torStatus === 'PENDING' }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const appr = Number(r.approvedHours) || 0;
  const pend = Number(r.pendingHours) || 0;
  const rate = r.sickRate == null ? null : Number(r.sickRate);
  const sup = r.superintendent?.name?.trim() || null;
  return {
    src: r,
    id: r.employeeID,
    name: (r.name ?? '').trim(),
    empID: String(r.altEmployeeID ?? r.employeeID ?? ''),
    sup,
    supFallback: !!sup && r.superintendent?.source === 'employee',
    days,
    appr,
    pend,
    total: Number(r.totalHours) || appr + pend,
    rate,
    rateState: rateState(appr, rate),
    stage: r.timesheet ? STAGE_LABEL[r.timesheet.stage] ?? 'Unsubmitted' : 'Unsubmitted',
    openable: !!r.timesheet,
  };
}

/* ------------------------------------------------------------------ console hand-off */

/** Board bucket the employee's timesheet sits in, for the ReviewIntent. The board
 *  sweeps every bucket when the employee isn't in this one, so it's a hint only. */
export function bucketForStage(stage: SickTimesheetStage | null | undefined): StatusKey {
  switch (stage) {
    case 'PENDING_APPROVAL': return 'submitted';
    case 'PAYROLL_PENDING': return 'supervisorApproved';
    case 'PAYROLL_APPROVED':
    case 'EXPORTED': return 'payrollApproved';
    default: return 'unsubmitted';
  }
}

/** "First Middle Last" → the ReviewIntent name shape. */
export function splitName(name: string): { first: string; last: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] ?? '', last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/* ------------------------------------------------------------------ scope */

export type Scope = 'needs' | 'pending' | 'all';
export const SCOPES: { key: Scope; label: string }[] = [
  { key: 'needs', label: 'Needs rate' },
  { key: 'pending', label: 'Has pending' },
  { key: 'all', label: 'All' },
];

export function inScope(row: Pick<ShRow, 'rateState' | 'pend'>, scope: Scope): boolean {
  if (scope === 'all') return true;
  return scope === 'needs' ? row.rateState === 'Needs rate' : row.pend > 0;
}

export function scopeCounts(rows: Pick<ShRow, 'rateState' | 'pend'>[]): Record<Scope, number> {
  const c: Record<Scope, number> = { needs: 0, pending: 0, all: rows.length };
  for (const r of rows) {
    if (inScope(r, 'needs')) c.needs++;
    if (inScope(r, 'pending')) c.pending++;
  }
  return c;
}

export function exportSuffix(scope: Scope): SickHoursExportSuffix | undefined {
  return scope === 'needs' ? 'NeedsRate' : scope === 'pending' ? 'HasPending' : undefined;
}

/** SickHours_Wk43_2026-10-12[_NeedsRate|_HasPending].xlsx — used when the server sends no file name. */
export function exportFileName(week: number, startDate: string, scope: Scope): string {
  const sfx = exportSuffix(scope);
  return `SickHours_Wk${week}_${startDate}${sfx ? `_${sfx}` : ''}.xlsx`;
}

/* ------------------------------------------------------------------ filters */

export interface ColumnFilters {
  emp: string;
  empId: string;
  sup: string[];
  /** Weekday indexes, 0 = Mon … 6 = Sun — survive a week change. */
  days: number[];
  rate: RateState[];
  rateMin: string;
  rateMax: string;
  ts: StageLabel[];
}

export type FilterCol = 'emp' | 'sup' | 'days' | 'rate' | 'ts';
/** A removable chip key — Employee ID has its own chip though it lives in the Employee popover. */
export type ChipKey = FilterCol | 'empId';

export const emptyFilters = (): ColumnFilters => ({
  emp: '', empId: '', sup: [], days: [], rate: [], rateMin: '', rateMax: '', ts: [],
});

const low = (s: string | null | undefined) => String(s ?? '').toLowerCase();
const stripHash = (s: string) => s.trim().replace(/^#/, '').trim();

/** Name, employee ID (a leading # is ignored) or superintendent. */
export function matchesSearch(row: ShRow, q: string): boolean {
  const t = stripHash(q).toLowerCase();
  if (!t) return true;
  return [row.name, row.empID, row.sup ?? ''].some((v) => low(v).includes(t));
}

export function matchesFilters(row: ShRow, f: ColumnFilters): boolean {
  const emp = f.emp.trim().toLowerCase();
  if (emp && !low(row.name).includes(emp)) return false;
  const empId = stripHash(f.empId).toLowerCase();
  if (empId && !low(row.empID).includes(empId)) return false;
  if (f.sup.length && !f.sup.includes(row.sup ?? NO_SUPER)) return false;
  if (f.days.length && !row.days.some((d) => f.days.includes(d.dow))) return false;
  if (f.rate.length && !f.rate.includes(row.rateState)) return false;
  // Min / max only match rows with a rate entered.
  const min = parseFloat(f.rateMin);
  if (f.rateMin !== '' && !Number.isNaN(min) && !(row.rateState === 'Entered' && row.rate != null && row.rate >= min)) return false;
  const max = parseFloat(f.rateMax);
  if (f.rateMax !== '' && !Number.isNaN(max) && !(row.rateState === 'Entered' && row.rate != null && row.rate <= max)) return false;
  if (f.ts.length && !f.ts.includes(row.stage)) return false;
  return true;
}

export function isColumnFiltered(f: ColumnFilters, col: FilterCol): boolean {
  switch (col) {
    case 'emp': return !!(f.emp.trim() || f.empId.trim());
    case 'sup': return f.sup.length > 0;
    case 'days': return f.days.length > 0;
    case 'rate': return f.rate.length > 0 || f.rateMin !== '' || f.rateMax !== '';
    case 'ts': return f.ts.length > 0;
  }
}

/** Clear one chip / column. Clearing 'emp' (the column) clears both Employee inputs. */
export function clearFilter(f: ColumnFilters, key: ChipKey): ColumnFilters {
  switch (key) {
    case 'emp': return { ...f, emp: '', empId: '' };
    case 'empId': return { ...f, empId: '' };
    case 'sup': return { ...f, sup: [] };
    case 'days': return { ...f, days: [] };
    case 'rate': return { ...f, rate: [], rateMin: '', rateMax: '' };
    case 'ts': return { ...f, ts: [] };
  }
}

export interface Chip { key: ChipKey; label: string; value: string; title?: string }

export function filterChips(f: ColumnFilters): Chip[] {
  const out: Chip[] = [];
  const q = (s: string) => `“${s.trim()}”`;
  const first = (a: string[]) => a[0] + (a.length > 1 ? ` +${a.length - 1}` : '');
  if (f.emp.trim()) out.push({ key: 'emp', label: 'Employee', value: q(f.emp) });
  if (f.empId.trim()) out.push({ key: 'empId', label: 'Employee ID', value: q(f.empId) });
  if (f.sup.length) out.push({ key: 'sup', label: 'Superintendent', value: first(f.sup), title: f.sup.join(', ') });
  if (f.days.length) {
    out.push({ key: 'days', label: 'Sick days', value: f.days.slice().sort((a, b) => a - b).map((i) => DOW[i]).join(', ') });
  }
  if (f.rate.length || f.rateMin !== '' || f.rateMax !== '') {
    const r = f.rateMin !== '' && f.rateMax !== ''
      ? `${money(f.rateMin)} – ${money(f.rateMax)}`
      : f.rateMin !== '' ? `≥ ${money(f.rateMin)}` : f.rateMax !== '' ? `≤ ${money(f.rateMax)}` : '';
    out.push({
      key: 'rate',
      label: 'Sick rate',
      value: [f.rate.length ? first(f.rate) : '', r].filter(Boolean).join(' · '),
      title: f.rate.join(', ') || undefined,
    });
  }
  if (f.ts.length) out.push({ key: 'ts', label: 'Timesheet', value: first(f.ts), title: f.ts.join(', ') });
  return out;
}

/* ------------------------------------------------------------------ sort */

export type SortKey = 'emp' | 'sup' | 'days' | 'appr' | 'pend' | 'total' | 'rate' | 'ts';
export interface SortState { key: SortKey; dir: 'asc' | 'desc' }

export const DEFAULT_SORT: SortState = { key: 'sup', dir: 'asc' };

export const SORT_LABEL: Record<SortKey, string> = {
  emp: 'Employee', sup: 'Superintendent', days: 'Sick days', appr: 'Approved hrs', pend: 'Pending hrs',
  total: 'Total hrs', rate: 'Sick rate', ts: 'Timesheet',
};

const NUMERIC: SortKey[] = ['appr', 'pend', 'total', 'rate'];

/** Clicking the active column flips it; a new column starts numbers desc, everything else asc. */
export function nextSort(cur: SortState, key: SortKey): SortState {
  if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: NUMERIC.includes(key) ? 'desc' : 'asc' };
}

const cmpText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Superintendent A–Z (blank last), then Employee A–Z, then id. Direction-independent. */
function tie(a: ShRow, b: ShRow): number {
  if (a.sup !== b.sup) {
    if (!a.sup) return 1;
    if (!b.sup) return -1;
    const c = cmpText(a.sup, b.sup);
    if (c) return c;
  }
  return cmpText(a.name, b.name) || a.id - b.id;
}

/** Entered rates by value; Pending only sorts below any rate, Needs rate below that. */
function rateKey(r: ShRow): number {
  if (r.rateState === 'Entered') return r.rate ?? 0;
  return r.rateState === 'Pending only' ? -1 : -2;
}

/** Sort a copy. */
export function sortRows(rows: ShRow[], sort: SortState): ShRow[] {
  const d = sort.dir === 'asc' ? 1 : -1;
  const num = (a: number, b: number) => (a - b) * d;
  const primary = (a: ShRow, b: ShRow): number => {
    switch (sort.key) {
      case 'emp': return cmpText(a.name, b.name) * d;
      case 'sup': {
        if (a.sup === b.sup) return 0;
        if (!a.sup) return 1;
        if (!b.sup) return -1;
        return cmpText(a.sup, b.sup) * d;
      }
      case 'days': {
        // Earliest sick day first, then fewer days first.
        const A = (a.days[0]?.dow ?? 7) * 10 + a.days.length;
        const B = (b.days[0]?.dow ?? 7) * 10 + b.days.length;
        return num(A, B);
      }
      case 'appr': return num(a.appr, b.appr);
      case 'pend': return num(a.pend, b.pend);
      case 'total': return num(a.total, b.total);
      case 'rate': return num(rateKey(a), rateKey(b));
      case 'ts': return num(STAGES.indexOf(a.stage), STAGES.indexOf(b.stage));
    }
  };
  return rows.slice().sort((a, b) => primary(a, b) || tie(a, b));
}

/* ------------------------------------------------------------------ counts */

export interface Summary { employees: number; approved: number; pending: number; needsRate: number }

/** Totals for exactly the rows shown (which is also what Export contains). */
export function summarize(rows: ShRow[]): Summary {
  let approved = 0;
  let pending = 0;
  let needsRate = 0;
  for (const r of rows) {
    approved += r.appr;
    pending += r.pend;
    if (r.rateState === 'Needs rate') needsRate++;
  }
  return { employees: rows.length, approved, pending, needsRate };
}

/** Distinct superintendent names across every row (sorted), then (none) last. */
export function supervisorOptions(rows: ShRow[]): string[] {
  const names = Array.from(new Set(rows.map((r) => r.sup).filter((s): s is string => !!s)));
  names.sort(cmpText);
  names.push(NO_SUPER);
  return names;
}

/** Popover counts: rows per superintendent / sick weekday / rate state / stage. */
export function columnCounts(rows: ShRow[]): {
  sup: Record<string, number>;
  days: Record<number, number>;
  rate: Record<string, number>;
  ts: Record<string, number>;
} {
  const sup: Record<string, number> = {};
  const days: Record<number, number> = {};
  const rate: Record<string, number> = {};
  const ts: Record<string, number> = {};
  const bump = <K extends string | number>(o: Record<K, number>, k: K) => { o[k] = (o[k] ?? 0) + 1; };
  for (const r of rows) {
    bump(sup, r.sup ?? NO_SUPER);
    new Set(r.days.map((d) => d.dow)).forEach((i) => bump(days, i));
    bump(rate, r.rateState);
    bump(ts, r.stage);
  }
  return { sup, days, rate, ts };
}

/* ------------------------------------------------------------------ chip fitting */

export interface ChipFit {
  /** How many day chips stay visible (from the start). */
  shown: number;
  /** Even the first chip doesn't fit — it truncates with an ellipsis. */
  solo: boolean;
}

/**
 * Day chips wrap onto at most `lines` lines (flex-wrap, greedy — same as the browser).
 * Given each chip's natural width, the `+n` chip's width, the gap and the available
 * width, keep as many leading chips as fit, leaving room for `+n` on the last line when
 * any are folded (at least one chip). Runs on render and resize only.
 */
export function fitDayChips(widths: number[], moreWidth: number, gap: number, avail: number, lines = 2): ChipFit {
  const n = widths.length;
  if (n === 0) return { shown: 0, solo: false };
  if (widths[0] > avail + 1) return { shown: 1, solo: true };
  // Greedy line packing; x = used width of the current line.
  let line = 1;
  let x = 0;
  const ends: number[] = []; // used width of the last line after each placed chip
  let placed = 0;
  for (let i = 0; i < n; i++) {
    const w = widths[i];
    if (x > 0 && x + gap + w <= avail + 1) {
      x += gap + w;
    } else if (x === 0 && w <= avail + 1) {
      x = w;
    } else if (line < lines && w <= avail + 1) {
      line += 1;
      x = w;
      ends.length = 0;
    } else {
      break;
    }
    ends.push(x);
    placed += 1;
  }
  if (placed === n) return { shown: n, solo: false };
  // Something folds: drop trailing chips off the last line until `+n` fits beside them.
  let shown = placed;
  while (ends.length > 0 && ends[ends.length - 1] + gap + moreWidth > avail + 1) {
    ends.pop();
    shown -= 1;
  }
  if (shown < 1) return { shown: 1, solo: true };
  return { shown, solo: false };
}
